import { useEffect, useState, useMemo, Fragment } from "react";
import api from "@/lib/api";
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus, ChevronRight, ChevronDown } from "lucide-react";

// Distinct, brand-aligned per-source palette. Unknown = slate gray.
const SOURCE_COLORS = {
  google_ads: "#1B2A4A",       // navy — biggest paid channel
  direct: "#2A9D8F",           // teal
  offline: "#8C7454",          // brown / taupe
  meta_ads: "#F58518",         // brand orange
  organic_search: "#6DC5E0",   // light cyan
  referral: "#4CA34C",         // medium green
  ai_referral: "#A3D65C",      // lime green
  organic_social: "#8B6BB0",   // purple
  unknown: "#8892A0",          // slate gray
  email: "#F5847A",            // coral
};
const colorFor = (slug) => SOURCE_COLORS[slug] || "#B9B0A2";
const fmtMoney = (v) => v == null ? "—" : `$${Math.round(v).toLocaleString()}`;
const fmtInt = (v) => v == null ? "—" : Math.round(v).toLocaleString();
const fmtPctPts = (v) => v == null ? "—" : `${v.toFixed(1)}%`;
const shortMonth = (p) => {
  if (!p || p.length < 7) return p;
  const [y, m] = p.split("-");
  return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][+m-1]} '${y.slice(2)}`;
};

const VIEWS = [
  { key: "volume",     label: "Volume" },
  { key: "share",      label: "Share %" },
  { key: "close_rate", label: "Close rate" },
  { key: "revenue",    label: "Revenue" },
  { key: "avg_deal",   label: "Avg deal" },
  { key: "velocity",   label: "Velocity" },
  { key: "funnel",     label: "Funnel" },
];
const RANGES = [
  { key: 3,  label: "3 mo" },
  { key: 6,  label: "6 mo" },
  { key: 12, label: "12 mo" },
  { key: 36, label: "All time" },
];

/**
 * DeltaChip — small MoM/vs-prior-period delta pill for KPI cards.
 * Inverted for metrics where lower is better (avg_days, CPL).
 */
function DeltaChip({ pct, invert = false, testid }) {
  if (pct == null || isNaN(pct)) return null;
  const isUp = pct > 0.5, isDown = pct < -0.5;
  const isGood = invert ? isDown : isUp;
  const isBad = invert ? isUp : isDown;
  const cls = isGood ? "text-evergreen bg-evergreen/10"
    : isBad ? "text-red-700 bg-red-700/10"
    : "text-midnight bg-midnight/10 opacity-70";
  const Icon = pct > 0 ? ArrowUpRight : pct < 0 ? ArrowDownRight : Minus;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cls}`}
          data-testid={testid}>
      <Icon className="w-3 h-3" />
      {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

function pctDelta(cur, prior) {
  if (cur == null || prior == null || prior === 0) return null;
  return ((cur - prior) / prior) * 100;
}

/** KPI card — number + label + delta chip. */
function KpiCard({ label, value, delta, invert = false, testid }) {
  return (
    <div className="bg-warm p-4 border-t-2 border-navy" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-60">{label}</div>
      <div className="mt-1 flex items-baseline gap-2 flex-wrap">
        <span className="text-2xl font-bold text-navy" data-testid={`${testid}-value`}>{value}</span>
        <DeltaChip pct={delta} invert={invert} testid={`${testid}-delta`} />
      </div>
      <div className="text-[9px] uppercase tracking-widest opacity-40 mt-0.5">vs prior period</div>
    </div>
  );
}

/** Toggle bar — segmented buttons. Reused for view + range switchers. */
function Toggle({ options, active, onChange, testid }) {
  return (
    <div className="inline-flex bg-warm-white border border-navy text-[11px] uppercase tracking-widest"
         role="tablist" data-testid={testid}>
      {options.map((opt) => (
        <button key={opt.key} type="button" onClick={() => onChange(opt.key)}
          data-testid={`${testid}-${opt.key}`}
          className={`px-3 py-1 border-r last:border-r-0 border-navy transition-colors ${active === opt.key ? "bg-navy text-warm" : "text-navy hover:bg-navy/10"}`}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Heat-map background for a cell — light green tint scaled by value in [min,max].
 * Text stays readable because opacity peaks at 0.35.
 */
function heatBg(v, min, max) {
  if (v == null || max === min) return "transparent";
  const n = Math.max(0, Math.min(1, (v - min) / (max - min)));
  return `rgba(76, 163, 76, ${(n * 0.35).toFixed(2)})`;
}

/**
 * TimeSeriesChart — renders the 6 non-funnel views by picking a per-metric
 * data-key on the channels. Uses stacked area for volume/share/revenue,
 * line for the three rate-style views.
 */
function TimeSeriesChart({ data, view }) {
  const channels = data.channels || [];
  const months = data.months || [];
  if (!channels.length) return <EmptyState />;

  // Sample-size gating for the noisy per-month rate views
  const MIN = 3;
  const topN = view === "close_rate" || view === "avg_deal" || view === "velocity"
    ? channels.slice(0, 5) : channels;

  const rows = months.map((period, i) => {
    const row = { period };
    const totalV = topN.reduce((a, c) => a + (c.volume[i] || 0), 0) || 1;
    for (const c of topN) {
      switch (view) {
        case "volume":     row[c.slug] = c.volume[i] || 0; break;
        case "share":      row[c.slug] = totalV ? +((c.volume[i] / totalV) * 100).toFixed(1) : 0; break;
        case "close_rate": row[c.slug] = c.volume[i] >= MIN ? c.close_rate[i] : null; break;
        case "revenue":    row[c.slug] = +((c.revenue[i] || 0).toFixed(0)); break;
        case "avg_deal":   row[c.slug] = c.closed[i] >= MIN ? c.avg_deal[i] : null; break;
        case "velocity":   row[c.slug] = c.closed[i] >= MIN ? c.avg_days[i] : null; break;
        default: break;
      }
    }
    return row;
  });

  const isStackedArea = view === "volume" || view === "share" || view === "revenue";
  const yFmt = view === "share" ? (v) => `${v}%`
    : view === "close_rate" ? (v) => v == null ? "—" : `${v}%`
    : view === "revenue" || view === "avg_deal" ? (v) => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${Math.round(v)}`
    : view === "velocity" ? (v) => `${v}d`
    : (v) => v;
  const tFmt = view === "share" ? (v) => `${v}%`
    : view === "close_rate" ? (v) => v == null ? "—" : `${v}%`
    : view === "revenue" || view === "avg_deal" ? (v) => fmtMoney(v)
    : view === "velocity" ? (v) => v == null ? "—" : `${v} days`
    : (v) => v?.toLocaleString();

  const label = {
    volume: "Leads · monthly",
    share: "Share of month · %",
    close_rate: `Close rate · top 5 · min ${MIN} leads/mo`,
    revenue: "Revenue · monthly ($)",
    avg_deal: `Avg deal size · top 5 · min ${MIN} deals/mo`,
    velocity: `Days to close · top 5 · min ${MIN} deals/mo · lower is better`,
  }[view];

  return (
    <>
      <div className="text-[10px] uppercase tracking-widest text-cobalt mb-1"
           data-testid="source-chart-label">{label}</div>
      <ResponsiveContainer width="100%" height={340}>
        {isStackedArea ? (
          <AreaChart data={rows} stackOffset={view === "share" ? "expand" : "none"}>
            <CartesianGrid stroke="#141B4D" strokeOpacity={0.1} vertical={false} />
            <XAxis dataKey="period" tick={{ fill: "#101820", fontSize: 11 }} tickFormatter={shortMonth} />
            <YAxis tick={{ fill: "#101820", fontSize: 11 }} tickFormatter={yFmt}
                   domain={view === "share" ? [0, 100] : undefined} />
            <Tooltip formatter={tFmt} labelFormatter={shortMonth}
              contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "8px 12px" }}
              itemStyle={{ color: "#F2E9DB" }} labelStyle={{ color: "#FFD100", fontWeight: 700 }} />
            <Legend wrapperStyle={{ fontSize: 11, color: "#101820" }} />
            {topN.slice().reverse().map((c) => (
              <Area key={c.slug} type="monotone" dataKey={c.slug} name={c.label}
                    stackId="1" stroke={colorFor(c.slug)} fill={colorFor(c.slug)}
                    fillOpacity={0.75} strokeWidth={1} isAnimationActive={false} />
            ))}
          </AreaChart>
        ) : (
          <LineChart data={rows}>
            <CartesianGrid stroke="#141B4D" strokeOpacity={0.1} vertical={false} />
            <XAxis dataKey="period" tick={{ fill: "#101820", fontSize: 11 }} tickFormatter={shortMonth} />
            <YAxis tick={{ fill: "#101820", fontSize: 11 }} tickFormatter={yFmt}
                   domain={view === "close_rate" ? [0, 100] : undefined} />
            <Tooltip formatter={tFmt} labelFormatter={shortMonth}
              contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "8px 12px" }}
              itemStyle={{ color: "#F2E9DB" }} labelStyle={{ color: "#FFD100", fontWeight: 700 }} />
            <Legend wrapperStyle={{ fontSize: 11, color: "#101820" }} />
            {topN.map((c) => (
              <Line key={c.slug} type="monotone" dataKey={c.slug} name={c.label}
                    stroke={colorFor(c.slug)} strokeWidth={2}
                    dot={false} connectNulls={false} isAnimationActive={false}
                    activeDot={{ r: 3, stroke: "#FFD100", strokeWidth: 2 }} />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </>
  );
}

/**
 * FunnelView — horizontal waterfall per source showing stage progression
 * (Created → Scheduled → Sat → Won) with leakage callouts below.
 */
function FunnelView({ data }) {
  const channels = data.channels || [];
  if (!channels.length) return <EmptyState />;
  const maxCreated = Math.max(1, ...channels.map((c) => c.funnel.created));
  return (
    <div className="space-y-5" data-testid="source-funnel">
      <div className="text-[10px] uppercase tracking-widest text-cobalt">
        Pipeline funnel · created → scheduled → sat → won · leakage below the bar
      </div>
      {channels.map((c) => {
        const f = c.funnel;
        const stages = [
          { key: "created", label: "Created", n: f.created, color: colorFor(c.slug) },
          { key: "meeting_scheduled", label: "Scheduled", n: f.meeting_scheduled, color: colorFor(c.slug) },
          { key: "meeting_sat", label: "Sat", n: f.meeting_sat, color: colorFor(c.slug) },
          { key: "closed_won", label: "Won", n: f.closed_won, color: "#4CA34C" },
        ];
        const losses = [
          { key: "meeting_missed", label: "Missed", n: f.meeting_missed, color: "#D97706" },
          { key: "disqualified", label: "DQ Financial", n: f.disqualified, color: "#F58518" },
          { key: "closed_lost", label: "Lost", n: f.closed_lost, color: "#C53030" },
          { key: "future_followup", label: "Future f/u", n: f.future_followup, color: "#1D57A5" },
        ].filter((s) => s.n > 0);
        return (
          <div key={c.slug} className="bg-warm p-3 border-l-4"
               style={{ borderColor: colorFor(c.slug) }}
               data-testid={`funnel-${c.slug}`}>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm font-bold text-navy">{c.label}</span>
              <span className="text-[10px] uppercase tracking-widest opacity-60">
                {f.created} created → {f.closed_won} won ·
                {" "}{f.created ? ((f.closed_won / f.created) * 100).toFixed(1) : "—"}% end-to-end
              </span>
            </div>
            <div className="flex items-stretch gap-1">
              {stages.map((s, i) => {
                const w = (s.n / maxCreated) * 100;
                return (
                  <div key={s.key} className="relative flex-1 min-w-[60px]"
                       data-testid={`funnel-${c.slug}-${s.key}`}>
                    <div className="h-8 flex items-center justify-center text-[11px] font-bold text-warm"
                         style={{ background: s.color, width: `${Math.max(w * 4, 25)}%`, opacity: 0.95 - i * 0.1 }}>
                      {s.n}
                    </div>
                    <div className="text-[9px] uppercase tracking-widest mt-1 opacity-70">{s.label}</div>
                  </div>
                );
              })}
            </div>
            {losses.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {losses.map((l) => (
                  <span key={l.key} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 text-warm"
                        style={{ background: l.color }}>
                    {l.label}: <strong>{l.n}</strong>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EmptyState() {
  return <div className="text-sm text-midnight opacity-60 italic py-10 text-center">
    No lead-source data in the selected window.
  </div>;
}

/**
 * LeadSourcesSection — self-contained Dashboard block:
 * KPI header · time-range picker · include-indirect toggle · 7 view tabs ·
 * time-series chart or funnel · summary table with drill-down.
 * Fetches /api/analytics/source-over-time whenever the window or filter changes.
 */
export default function LeadSourcesSection() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("volume");
  const [months, setMonths] = useState(12);
  const [includeIndirect, setIncludeIndirect] = useState(false);
  const [expandedSlug, setExpandedSlug] = useState(null);

  useEffect(() => {
    api.get(`/analytics/source-over-time?months=${months}&include_indirect=${includeIndirect}`)
      .then((r) => setData(r.data));
  }, [months, includeIndirect]);

  const wt = data?.window_totals || {};
  const pt = data?.prior_window_totals || {};

  // Heat-map ranges for table shading
  const heatRanges = useMemo(() => {
    const chans = data?.channels || [];
    const cr = chans.map((c) => c.close_rate_pct).filter((x) => x != null);
    const ad = chans.map((c) => c.total_avg_deal).filter((x) => x != null);
    return {
      close_rate: [Math.min(...cr, 0), Math.max(...cr, 100)],
      avg_deal: [Math.min(...ad, 0), Math.max(...ad, 1)],
    };
  }, [data]);
  const maxLeads = Math.max(1, ...(data?.channels || []).map((c) => c.total_volume));
  const maxRev   = Math.max(1, ...(data?.channels || []).map((c) => c.total_revenue));

  if (!data) return <div className="p-10 text-sm opacity-60">Loading lead sources…</div>;

  return (
    <div className="bg-warm-tint border-t-4 border-navy p-6 space-y-5" data-testid="source-pie-chart">
      {/* Header — title + toggle + range picker */}
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-navy">Lead sources over time</h2>
          <div className="text-[10px] text-midnight opacity-70 italic mt-1">
            HubSpot deals-created per channel per month (Meta-aware classifier). Contacts not yet synced — deals-created proxy for leads.
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy cursor-pointer"
                 data-testid="include-indirect-label">
            <input type="checkbox" checked={includeIndirect}
                   onChange={(e) => setIncludeIndirect(e.target.checked)}
                   data-testid="include-indirect-toggle" className="accent-navy" />
            Include indirect sources
          </label>
          <Toggle options={RANGES} active={months} onChange={setMonths} testid="range-toggle" />
        </div>
      </div>

      {/* KPI header — 5 cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="source-kpi-row">
        <KpiCard label="Total leads" value={fmtInt(wt.volume)}
          delta={pctDelta(wt.volume, pt.volume)} testid="kpi-leads" />
        <KpiCard label="Total closed" value={fmtInt(wt.closed)}
          delta={pctDelta(wt.closed, pt.closed)} testid="kpi-closed" />
        <KpiCard label="Close rate" value={fmtPctPts(wt.close_rate_pct)}
          delta={pctDelta(wt.close_rate_pct, pt.close_rate_pct)} testid="kpi-close-rate" />
        <KpiCard label="Total revenue" value={fmtMoney(wt.revenue)}
          delta={pctDelta(wt.revenue, pt.revenue)} testid="kpi-revenue" />
        <KpiCard label="Avg days to close" value={wt.avg_days_to_close != null ? `${wt.avg_days_to_close}d` : "—"}
          delta={pctDelta(wt.avg_days_to_close, pt.avg_days_to_close)} invert testid="kpi-days" />
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-2 overflow-x-auto">
        <Toggle options={VIEWS} active={view} onChange={setView} testid="source-view-toggle" />
      </div>

      {/* Chart body */}
      {view === "funnel" ? <FunnelView data={data} /> : <TimeSeriesChart data={data} view={view} />}

      {/* Filter-off transparency note */}
      {!includeIndirect && data.indirect_hidden_count > 0 && (
        <div className="text-[11px] italic opacity-60" data-testid="indirect-hidden-note">
          Showing paid &amp; organic channels only. <strong>{data.indirect_hidden_count}</strong> leads from indirect sources (Offline, Direct, Unknown, Email) are hidden. Toggle above to include.
        </div>
      )}

      {/* Summary table with drill-down */}
      <div className="overflow-x-auto">
        <table className="w-full il-table text-[13px]" data-testid="source-table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Source</th>
              <th>Leads</th>
              <th>Closed</th>
              <th>Close rate</th>
              <th>Revenue</th>
              <th>Avg deal</th>
              <th>Avg days</th>
            </tr>
          </thead>
          <tbody>
            {(data.channels || []).map((s) => {
              const isOpen = expandedSlug === s.slug;
              return (
                <Fragment key={s.slug}>
                  <tr onClick={() => setExpandedSlug(isOpen ? null : s.slug)}
                      data-testid={`source-row-${s.slug}`}
                      style={{ cursor: "pointer" }}>
                    <td className="text-navy">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td><span className="inline-block w-2.5 h-2.5 mr-2" style={{ background: colorFor(s.slug) }} /><strong className="text-navy">{s.label}</strong></td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span>{fmtInt(s.total_volume)}</span>
                        <div className="h-1.5 bg-navy/10 relative flex-1 max-w-[80px]">
                          <div className="absolute inset-y-0 left-0" style={{ width: `${(s.total_volume / maxLeads) * 100}%`, background: colorFor(s.slug) }} />
                        </div>
                      </div>
                    </td>
                    <td>{fmtInt(s.total_closed)}</td>
                    <td style={{ background: heatBg(s.close_rate_pct, heatRanges.close_rate[0], heatRanges.close_rate[1]) }}>{fmtPctPts(s.close_rate_pct)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span>{fmtMoney(s.total_revenue)}</span>
                        <div className="h-1.5 bg-navy/10 relative flex-1 max-w-[80px]">
                          <div className="absolute inset-y-0 left-0" style={{ width: `${(s.total_revenue / maxRev) * 100}%`, background: colorFor(s.slug) }} />
                        </div>
                      </div>
                    </td>
                    <td style={{ background: heatBg(s.total_avg_deal, heatRanges.avg_deal[0], heatRanges.avg_deal[1]) }}>{fmtMoney(s.total_avg_deal)}</td>
                    <td>{s.total_avg_days != null ? `${s.total_avg_days}d` : "—"}</td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`campaigns-${s.slug}`}>
                      <td colSpan={8} className="bg-warm p-3">
                        <div className="text-[10px] uppercase tracking-widest text-cobalt mb-2">
                          Top campaigns (hs_analytics_source_data_1) — top 10 by leads
                        </div>
                        {s.campaigns.length === 0 ? (
                          <div className="text-xs opacity-60 italic">No campaign detail for this source.</div>
                        ) : (
                          <table className="w-full il-table text-[12px]">
                            <thead>
                              <tr>
                                <th>Campaign</th>
                                <th>Leads</th>
                                <th>Closed</th>
                                <th>Close rate</th>
                                <th>Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {s.campaigns.map((c) => (
                                <tr key={c.campaign}>
                                  <td className="text-navy">{c.campaign}</td>
                                  <td>{fmtInt(c.leads)}</td>
                                  <td>{fmtInt(c.closed)}</td>
                                  <td>{c.leads ? fmtPctPts((c.closed / c.leads) * 100) : "—"}</td>
                                  <td>{fmtMoney(c.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer — data freshness */}
      <div className="text-[10px] uppercase tracking-widest opacity-40" data-testid="data-freshness">
        HubSpot data · last synced {data.last_synced
          ? new Date(data.last_synced).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
          : "unknown"}
      </div>
    </div>
  );
}
